import React, { useCallback, useEffect, useMemo, useState } from 'react'
import apiClient from '../../services/apiClient'
import {
  CBadge,
  CCard,
  CCardBody,
  CCardText,
  CRow,
  CImage,
  CCol,
  CSpinner,
  CPlaceholder,
  CTooltip,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilBell, cilBan, cilPowerStandby } from '@coreui/icons'
import aoiPic1 from 'src/assets/brand/ic_launcher.png'
import BadgeComponent from './BadgeComponent'
import { CChartLine } from '@coreui/react-chartjs'
import { getStyle, hexToRgba } from '@coreui/utils'
import PropTypes from 'prop-types'
import {
  buildApiUrl,
  calculateMonthlyTarget,
  readModuleFlagsFromStorage,
  resolveModuleFlags,
} from '../../utils/getConfiguration'
import useThrottleFetch from '../../hooks/useThrottleFetch'
import { useTranslation } from 'react-i18next'

const LARGE_LAYOUT_QUERY = '(min-width: 992px)'
const XL_LAYOUT_QUERY = '(min-width: 1200px)'

const WidgetRemindersSum = ({ uniqueKey }) => {
  const { t, i18n } = useTranslation()
  const [toprecommendations, setTopRecommendations] = useState([])
  const [activeRecommendations, setActiveRecommendations] = useState([])
  const [ignoredRecommendations, setIgnoredRecommendations] = useState([])
  const [switchedOffRecommendations, setSwitchedOffRecommendations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [accountContext, setAccountContext] = useState({ accountId: null })
  const { accountId } = accountContext
  const [isLargeLayout, setIsLargeLayout] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia?.(LARGE_LAYOUT_QUERY)?.matches ?? false
  })
  const [isXLLayout, setIsXLLayout] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia?.(XL_LAYOUT_QUERY)?.matches ?? false
  })

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined
    }

    const largeQuery = window.matchMedia(LARGE_LAYOUT_QUERY)
    const xlQuery = window.matchMedia(XL_LAYOUT_QUERY)

    const onLargeChange = (event) => setIsLargeLayout(event.matches)
    const onXLChange = (event) => setIsXLLayout(event.matches)

    const add = (query, handler) => {
      if (query.addEventListener) {
        query.addEventListener('change', handler)
        return () => query.removeEventListener('change', handler)
      }
      query.addListener(handler)
      return () => query.removeListener(handler)
    }

    const removeLarge = add(largeQuery, onLargeChange)
    const removeXL = add(xlQuery, onXLChange)

    onLargeChange(largeQuery)
    onXLChange(xlQuery)

    return () => {
      removeLarge()
      removeXL()
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const readStorage = () => {
      setAccountContext({
        accountId: window.localStorage.getItem('account_ID'),
      })
    }

    readStorage()
    window.addEventListener('storage', readStorage)

    return () => {
      window.removeEventListener('storage', readStorage)
    }
  }, [])

  const fetchRemindersData = useCallback(async () => {
    if (!accountId) {
      setTopRecommendations([])
      setActiveRecommendations([])
      setIgnoredRecommendations([])
      setSwitchedOffRecommendations([])
      setError(null)
      setLoading(false)
      return
    }

    try {
      const requests = [
        apiClient.get(buildApiUrl('remindersTopAll', { account_ID: accountId })),
        apiClient.get(buildApiUrl('reminders', { account_ID: accountId, grp_ID: 1 })),
        apiClient.get(buildApiUrl('accountRemindersIgnored', { account_ID: accountId })),
        apiClient.get(buildApiUrl('accountRemindersSwitchedOff', { account_ID: accountId })),
      ]

      const [topRes, activeRes, ignoredRes, switchedRes] = await Promise.all(requests)

      setTopRecommendations(Array.isArray(topRes.data) ? topRes.data : [])
      setActiveRecommendations(Array.isArray(activeRes.data) ? activeRes.data : [])
      setIgnoredRecommendations(Array.isArray(ignoredRes.data) ? ignoredRes.data : [])
      setSwitchedOffRecommendations(Array.isArray(switchedRes.data) ? switchedRes.data : [])
      setError(null)
    } catch (fetchError) {
      console.error('Error fetching reminder summary data', fetchError)
      setTopRecommendations([])
      setActiveRecommendations([])
      setIgnoredRecommendations([])
      setSwitchedOffRecommendations([])
      setError('Unable to load reminder summary')
    } finally {
      setLoading(false)
    }
  }, [accountId])

  const throttledFetch = useThrottleFetch(fetchRemindersData, 2000)

  useEffect(() => {
    setLoading(true)
    throttledFetch()

    return () => {
      throttledFetch.cancel?.()
    }
  }, [throttledFetch, uniqueKey])

  const [scoreRefreshKey, setScoreRefreshKey] = useState(0)

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const handleScoreRefresh = () => {
      setScoreRefreshKey((prev) => prev + 1)
    }

    window.addEventListener('app:score-updated', handleScoreRefresh)
    window.addEventListener('storage', handleScoreRefresh)

    return () => {
      window.removeEventListener('app:score-updated', handleScoreRefresh)
      window.removeEventListener('storage', handleScoreRefresh)
    }
  }, [])

  const scores = useMemo(() => {
    void uniqueKey
    void scoreRefreshKey
    return Array.from({ length: 12 }, (_, i) => Number(localStorage.getItem(`score${i + 1}`)) || 0)
  }, [uniqueKey, scoreRefreshKey])
  const cumulativeScores = useMemo(
    () =>
      scores.reduce((acc, val) => {
        acc.push((acc.length ? acc[acc.length - 1] : 0) + val)
        return acc
      }, []),
    [scores],
  )
  const accountScore = useMemo(
    () => cumulativeScores[cumulativeScores.length - 1] || 0,
    [cumulativeScores],
  )

  const [moduleFlags, setModuleFlags] = useState(() => readModuleFlagsFromStorage())

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const readFlags = () => {
      setModuleFlags(readModuleFlagsFromStorage())
    }

    readFlags()
    window.addEventListener('storage', readFlags)

    return () => {
      window.removeEventListener('storage', readFlags)
    }
  }, [])

  const resolvedModuleFlags = useMemo(() => resolveModuleFlags(moduleFlags), [moduleFlags])
  const monthlyTarget = calculateMonthlyTarget(resolvedModuleFlags)
  const hasTargets = monthlyTarget > 0

  const currentMonth = new Date().getMonth() + 1
  const currentMonthName = useMemo(() => {
    const lang = (i18n.language || 'en').split('-')[0]
    return new Date().toLocaleString(lang, { month: 'long' }).toUpperCase()
  }, [i18n.language])
  const currentTarget = currentMonth * monthlyTarget
  const getProgressBadge = () => {
    if (!hasTargets) return <BadgeComponent priority={2} />
    if (accountScore < currentTarget - 10) return <BadgeComponent priority={1} />
    if (accountScore < currentTarget) return <BadgeComponent priority={2} />
    return <BadgeComponent priority={4} />
  }
  const { progressTooltip, progressStatusLabel } = useMemo(() => {
    if (!hasTargets) {
      return {
        progressTooltip: 'Monthly targets are not configured yet.',
        progressStatusLabel: 'Targets not configured',
      }
    }

    const delta = Math.max(0, Math.ceil(currentTarget - accountScore))
    if (delta === 0) {
      return {
        progressTooltip: "Great job! You have met or exceeded this month's target.",
        progressStatusLabel: 'Target achieved',
      }
    }

    const pluralRules = new Intl.PluralRules('en-US')
    const pluralCategory = pluralRules.select(delta)
    const pointsLabel = pluralCategory === 'one' ? 'point' : 'points'
    const formattedDelta = new Intl.NumberFormat('en-US').format(delta)

    return {
      progressTooltip: `Need ${formattedDelta} more ${pointsLabel} to reach the ${currentMonthName} target.`,
      progressStatusLabel: `Need ${formattedDelta} more ${pointsLabel} this month`,
    }
  }, [accountScore, currentMonthName, currentTarget, hasTargets])

  const recommendedSeries = useMemo(() => {
    if (!hasTargets) {
      return Array(12).fill(0)
    }
    return Array.from({ length: 12 }, (_, i) => (i + 1) * monthlyTarget)
  }, [hasTargets, monthlyTarget])

  const criticalSeries = useMemo(() => {
    if (!hasTargets) {
      return Array(12).fill(0)
    }
    return Array(12).fill(12 * monthlyTarget * 0.7)
  }, [hasTargets, monthlyTarget])

  const chartDatasets = useMemo(() => {
    const datasets = [
      {
        label: t('remindersSummary.chart.progress'),
        backgroundColor: hexToRgba(getStyle('--cui-info'), 10),
        borderColor: getStyle('--cui-info'),
        pointHoverBackgroundColor: getStyle('--cui-info'),
        borderWidth: 2,
        data: cumulativeScores,
        fill: true,
      },
    ]
    if (hasTargets) {
      datasets.push(
        {
          label: t('remindersSummary.chart.recommended'),
          backgroundColor: 'transparent',
          borderColor: getStyle('--cui-success'),
          pointHoverBackgroundColor: getStyle('--cui-success'),
          borderWidth: 2,
          data: recommendedSeries,
        },
        {
          label: t('remindersSummary.chart.critical'),
          backgroundColor: 'transparent',
          borderColor: getStyle('--cui-danger'),
          pointHoverBackgroundColor: getStyle('--cui-danger'),
          borderWidth: 1,
          borderDash: [8, 5],
          data: criticalSeries,
        },
      )
    }
    return datasets
  }, [cumulativeScores, criticalSeries, hasTargets, recommendedSeries, t])

  const maxDataValue = useMemo(() => {
    const values = [...cumulativeScores]
    if (hasTargets) {
      values.push(...recommendedSeries, ...criticalSeries)
    }
    return Math.max(...values, 1)
  }, [criticalSeries, cumulativeScores, hasTargets, recommendedSeries])

  const yMax = useMemo(() => {
    const buffer = Math.max(10, Math.ceil(maxDataValue * 0.1))
    const rawMax = maxDataValue + buffer
    const step = rawMax <= 100 ? 10 : rawMax <= 250 ? 25 : 50
    return Math.ceil(rawMax / step) * step || step
  }, [maxDataValue])

  const yStep = useMemo(() => Math.max(10, Math.ceil(yMax / 6)), [yMax])

  const chartData = useMemo(
    () => ({
      labels: Array.from({ length: 12 }, (_, i) => (i + 1).toString()),
      datasets: chartDatasets,
    }),
    [chartDatasets],
  )

  const chartOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
      },
      scales: {
        x: { grid: { drawOnChartArea: false } },
        y: {
          ticks: {
            beginAtZero: true,
            maxTicksLimit: 5,
            stepSize: yStep,
          },
          suggestedMax: yMax,
        },
      },
      elements: {
        line: { tension: 0.4 },
        point: {
          radius: 0,
          hitRadius: 10,
          hoverRadius: 4,
          hoverBorderWidth: 3,
        },
      },
    }),
    [yMax, yStep],
  )

  const legendItems = useMemo(() => {
    if (!isLargeLayout) return []
    return chartDatasets
      .filter((dataset) => dataset?.label)
      .map((dataset) => ({
        label: dataset.label,
        color: dataset.borderColor || dataset.backgroundColor || '#6c757d',
        dash: Array.isArray(dataset.borderDash) && dataset.borderDash.length > 0,
        line: dataset.fill === false || dataset.backgroundColor === 'transparent',
      }))
  }, [chartDatasets, isLargeLayout])

  const activeCount = activeRecommendations.length
  const ignoredCount = ignoredRecommendations.length
  const switchedOffCount = switchedOffRecommendations.length
  const statsSummary = useMemo(
    () => [
      {
        label: 'Active reminders',
        count: activeCount,
        badgeColor: 'info',
        icon: cilBell,
        variant: 'info',
        translationKey: 'remindersSummary.stats.active',
      },
      {
        label: 'Ignored reminders',
        count: ignoredCount,
        badgeColor: 'warning',
        icon: cilBan,
        variant: 'warning',
        translationKey: 'remindersSummary.stats.ignored',
      },
      {
        label: 'Switched reminders',
        count: switchedOffCount,
        badgeColor: 'secondary',
        icon: cilPowerStandby,
        variant: 'secondary',
        translationKey: 'remindersSummary.stats.switchedOff',
      },
    ],
    [activeCount, ignoredCount, switchedOffCount],
  )
  const hasTopData = toprecommendations.length > 0

  return (
    <CCard className="col-md-12 border-0 reminder-summary-card">
      {loading ? (
        <CCardBody className="p-3 p-lg-4" aria-busy="true" aria-live="polite">
          <div className="d-flex align-items-center gap-2 text-muted mb-3">
            <CSpinner color="primary" variant="grow" size="sm" />
            <span>Loading reminder summary…</span>
          </div>
          <div className="ratio ratio-16x9 mb-3 placeholder-glow">
            <CPlaceholder component="div" animation="wave" className="w-100 h-100 rounded" />
          </div>
          <CRow className="g-2 g-md-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <CCol key={index} xs={12} sm={6} md={4}>
                <div className="reminder-stat-card placeholder-glow">
                  <div className="reminder-stat-content">
                    <CPlaceholder
                      component="span"
                      animation="wave"
                      className="placeholder rounded-circle reminder-stat-icon-placeholder"
                    />
                    <CPlaceholder component="span" animation="wave" className="placeholder col-6" />
                  </div>
                  <CPlaceholder
                    component="span"
                    animation="wave"
                    className="reminder-stat-badge placeholder col-2"
                  />
                </div>
              </CCol>
            ))}
          </CRow>
        </CCardBody>
      ) : error ? (
        <CCardText className="alert alert-danger text-center mb-0">{error}</CCardText>
      ) : (
        <CCardBody className="p-3 p-lg-4">
          {hasTopData ? (
            <>
              <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                  <CImage
                    rounded
                    orientation="top"
                    loading="lazy"
                    src={aoiPic1}
                    width={48}
                    height={48}
                    alt="Progress"
                  />
                  <CTooltip content={progressTooltip} placement="top">
                    <span
                      role="status"
                      aria-live="polite"
                      aria-label={progressStatusLabel}
                      className="d-inline-flex align-items-center"
                    >
                      {getProgressBadge()}
                    </span>
                  </CTooltip>
                </div>
                <div className="flex-grow-1">
                  <CCardText className="mb-0 fw-semibold">
                    {t('remindersSummary.yearlyProgress', 'Yearly PROGRESS of categories', {
                      month: currentMonthName,
                    })}
                  </CCardText>
                </div>
              </div>
              <div className="ratio ratio-16x9 mb-3">
                <CChartLine data={chartData} options={chartOptions} />
              </div>
              {isLargeLayout && legendItems.length > 0 && (
                <div
                  className={`reminder-summary-legend${isXLLayout ? ' reminder-summary-legend--xl' : ''}`}
                  role="list"
                >
                  {legendItems.map(({ label, color, dash, line }) => {
                    const swatchClasses = ['reminder-summary-legend__swatch']
                    if (line) swatchClasses.push('reminder-summary-legend__swatch--line')
                    if (dash) swatchClasses.push('reminder-summary-legend__swatch--dashed')

                    return (
                      <div key={label} className="reminder-summary-legend__item" role="listitem">
                        <span
                          className={swatchClasses.join(' ')}
                          style={{ color, borderColor: color }}
                        />
                        <span>{label}</span>
                      </div>
                    )
                  })}
                </div>
              )}
              {!hasTargets && (
                <CCardText className="text-center text-muted">
                  {t(
                    'remindersSummary.noTargets',
                    'Enable at least one module to see recommended targets.',
                  )}
                </CCardText>
              )}
            </>
          ) : (
            <CCardText className="alert alert-info text-center mb-3">
              {t(
                'remindersSummary.noProgress',
                'No progress data available yet. Complete some reminders to populate this chart.',
              )}
            </CCardText>
          )}
          <hr />
          <CRow className="g-2 g-md-3">
            {statsSummary.map(({ label, count, badgeColor, icon, variant, translationKey }) => (
              <CCol key={label} xs={12} sm={6} md={6} lg={6} xl={12}>
                <div className="reminder-stat-card">
                  <div className="reminder-stat-content">
                    <span
                      className={`reminder-stat-icon reminder-stat-icon--${variant}`}
                      aria-hidden="true"
                    >
                      <CIcon icon={icon} size="lg" />
                    </span>
                    <span className="reminder-stat-label">
                      {translationKey ? t(translationKey, label) : label}
                    </span>
                  </div>
                  <CBadge color={badgeColor} className="reminder-stat-badge">
                    {count}
                  </CBadge>
                </div>
              </CCol>
            ))}
          </CRow>
        </CCardBody>
      )}
    </CCard>
  )
}
WidgetRemindersSum.propTypes = {
  uniqueKey: PropTypes.number.isRequired, // uniqueKey musí být číslo a je povinný
}

export default WidgetRemindersSum
